import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { FONTS, SECONDARY_COLOR } from "../../constants";
import { stateTypeF } from "../../state";

const baseStyle = {
	flex: 1,
	display: "flex",
	alignItems: "center",
	padding: "0px",
	borderWidth: 2,
	borderRadius: 12,
	borderColor: "#0063b4",
	borderStyle: "dashed",
	backgroundColor: "#ffffff",
	color: "#a0b4d2",
	outline: "none",
	transition: "border .24s ease-in-out",
	height: "100%",
	width: "100%",
};

const activeStyle = {
	borderColor: "#2a4255",
};

const acceptStyle = {
	borderColor: "#00e676",
};

const rejectStyle = {
	borderColor: "#ff1744",
};

function arrayBufferToString(
	buffer: ArrayBuffer,
	encoding: string,
	callback: Function,
	state: any
) {
	if (buffer === null) {
		return;
	}
	var blob = new Blob([buffer], { type: "text/plain" });
	var reader = new FileReader();
	reader.onload = function (evt: ProgressEvent<FileReader>) {
		callback(evt.target.result, state);
	};
	reader.readAsText(blob, encoding);
}

export const useStyles = makeStyles((theme: Theme) => {
	return createStyles({
		root: {
			width: "100%",
		},
		paper: {
			flex: 1,
			display: "flex",
			alignItems: "center",
			padding: "0px",
			borderWidth: 2,
			borderRadius: 12,
			borderColor: "#212c3f",
			borderStyle: "dashed",
			backgroundColor: "#ffffff",
			color: SECONDARY_COLOR,
			outline: "none",
			transition: "0.3s",
			height: "100%",
			width: "100%",
			"&:hover": {
				backgroundColor: "rgba(0,0,0,0.05)",
				color: SECONDARY_COLOR,
			},
			cursor: "pointer",
		},
		palette: {
			primary: "blue",
		},
		text: { width: "100%", textAlign: "center", fontFamily: FONTS },
	});
});

type StyledDropzoneType = {
	state: stateTypeF;
	callbackFunction: Function;
	saveOpen: boolean;
	button?;
	isNewDash?: boolean;
};

export function StyledDropzone({
	state,
	callbackFunction,
	saveOpen,
	button,
	isNewDash = false,
}: StyledDropzoneType) {
	const onDrop = useCallback((acceptedFiles) => {
		acceptedFiles.forEach((file: any) => {
			const reader = new FileReader();
			reader.onabort = () => console.log("file reading was aborted");
			reader.onerror = () => console.log("file reading has failed");
			reader.onload = () => {
				// Do whatever you want with the file contents
				const binaryStr = reader.result;
				localStorage.setItem("tempFileName", file.name);
				if (typeof binaryStr == "string") {
					return;
				}
				arrayBufferToString(
					binaryStr,
					"UTF-8",
					callbackFunction,
					state
				);
			};
			reader.readAsArrayBuffer(file);
		});
	}, []);

	const onDropRejected = (rejectedFiles) => {
		const ISP = state.getISP();
		const fileLimit = ISP ? "25" : "2";
		const additional = ISP ? "." : " on Free Tier.";

		state.setRejectedReason(
			"The file size limit is 25MB, please select another file."
		);
		state.setIsRejectedDropOpen(true);
	};

	const ISP = state.getISP();

	const {
		getRootProps,
		getInputProps,
		isDragActive,
		isDragAccept,
		isDragReject,
		fileRejections,
		open,
	} = useDropzone({
		onDrop,
		onDropRejected,
		accept: "text/csv",
		maxFiles: 1,
		multiple: false,
		maxSize: 2.5e7,
	});
	const classes = useStyles();
	useEffect(() => state.setDropzoneOpen(open, saveOpen), []);

	button =
		button === undefined ? (
			<div {...getRootProps()} className={classes.paper}>
				<input {...getInputProps()} />
				<div className={classes.text}>
					Drag 'n' drop your CSV data here
				</div>
			</div>
		) : (
			<div {...getRootProps()}>
				<input {...getInputProps()} />
				{button}
			</div>
		);

	return button;
}

export default StyledDropzone;
